import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { X, Plus, Trash2, Loader2, ShieldAlert, Pill, ClipboardCheck, ChevronDown, ChevronUp, FileDown } from "lucide-react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import BodyDiagram, { REGION_LABEL } from "./BodyDiagram";
import { Switch } from "@/components/ui/switch";
import CheckinForm from "./CheckinForm";
import CheckinTimeline from "./CheckinTimeline";
import BluetoothTempButton from "./BluetoothTempButton";

const SEVERITIES = [
  { key: "early_onset", label: "Early onset", hint: "Swelling & redness" },
  { key: "severe",      label: "Severe",      hint: "Fever, chills, temperature" },
];
const today = () => format(new Date(), "yyyy-MM-dd");

function YesNoPill({ value, onChange, testId }) {
  return (
    <div className="flex gap-1.5" data-testid={testId}>
      {[
        { v: true,  label: "Yes", cls: "bg-[#E63946] text-white border-[#E63946]" },
        { v: false, label: "No",  cls: "bg-kawaii-mint/60 text-kawaii-text border-kawaii-mint" },
      ].map((o) => {
        const active = value === o.v;
        return (
          <button
            type="button" key={String(o.v)}
            onClick={() => onChange(active ? null : o.v)}
            data-testid={`${testId}-${o.v ? 'yes' : 'no'}`}
            className={`kawaii-btn px-3.5 py-1 rounded-full text-xs font-body font-semibold border ${
              active ? o.cls : 'bg-white text-kawaii-textSoft border-kawaii-border'
            }`}
          >{o.label}</button>
        );
      })}
    </div>
  );
}

function SymptomRow({ label, value, onChange, testId, danger = false }) {
  return (
    <div className={`flex items-center justify-between gap-3 p-3 rounded-2xl border ${
      value === true && danger ? 'border-[#E63946]/60 bg-[#FFE5E5]/40' : 'border-kawaii-border bg-white'
    }`}>
      <span className="text-sm font-body text-kawaii-text pr-2">{label}</span>
      <YesNoPill value={value} onChange={onChange} testId={testId} />
    </div>
  );
}

function PastEpisodeForm({ onCancel, onSaved, initialDate }) {
  const [startDate, setStartDate] = useState(initialDate || today());
  const [endDate, setEndDate] = useState(initialDate || today());
  const [severity, setSeverity] = useState("early_onset");
  const [regions, setRegions] = useState([]);
  const [hospitalized, setHospitalized] = useState(null);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (startDate > endDate) return toast.error("End date must be after start date");
    setSaving(true);
    try {
      const { data } = await api.post("/cellulitis", {
        start_date: startDate,
        end_date: endDate,
        body_parts: regions,
        severity,
        on_antibiotics: false,
        hospitalized: hospitalized === true,
        notes,
      });
      toast.success("Past episode added to your history ♡");
      onSaved(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save");
    } finally { setSaving(false); }
  };

  return (
    <motion.form
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      onSubmit={submit}
      className="rounded-2xl bg-white border border-[#E63946]/30 p-5 mb-6"
      data-testid="past-cellulitis-form"
    >
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[10px] uppercase tracking-[0.25em] text-kawaii-textSoft font-bold">Back-fill a past cellulitis episode</span>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-kawaii-textSoft font-semibold">Started</label>
          <input
            type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
            max={today()}
            data-testid="past-start-date"
            className="mt-2 w-full px-3 py-2 rounded-2xl border border-kawaii-border bg-white font-body text-sm text-kawaii-text focus:outline-none focus:ring-2 focus:ring-[#E63946]/60"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-kawaii-textSoft font-semibold">Resolved</label>
          <input
            type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
            max={today()} min={startDate}
            data-testid="past-end-date"
            className="mt-2 w-full px-3 py-2 rounded-2xl border border-kawaii-border bg-white font-body text-sm text-kawaii-text focus:outline-none focus:ring-2 focus:ring-[#E63946]/60"
          />
        </div>
        <div className="md:col-span-2">
          <label className="text-xs uppercase tracking-[0.2em] text-kawaii-textSoft font-semibold">Stage</label>
          <div className="mt-2 flex gap-2">
            {SEVERITIES.map((s) => (
              <button
                type="button" key={s.key}
                onClick={() => setSeverity(s.key)}
                data-testid={`past-severity-${s.key}`}
                className={`kawaii-btn flex-1 py-2 px-2 rounded-2xl text-left border ${
                  severity === s.key ? 'bg-[#E63946] text-white border-[#E63946]' : 'bg-white text-kawaii-text border-kawaii-border'
                }`}
              >
                <div className="text-xs font-heading font-semibold uppercase tracking-wider">{s.label}</div>
                <div className={`text-[10px] font-body mt-0.5 ${severity === s.key ? 'text-white/85' : 'text-kawaii-textSoft'}`}>{s.hint}</div>
              </button>
            ))}
          </div>
        </div>
        <div className="md:col-span-2">
          <label className="text-xs uppercase tracking-[0.2em] text-kawaii-textSoft font-semibold">Hospitalized?</label>
          <div className="mt-2 flex gap-2" data-testid="past-hospitalized">
            {[
              { v: true, label: "Yes" }, { v: false, label: "No" },
            ].map((o) => {
              const active = hospitalized === o.v;
              return (
                <button
                  type="button" key={String(o.v)}
                  onClick={() => setHospitalized(active ? null : o.v)}
                  data-testid={`past-hospitalized-${o.v ? 'yes' : 'no'}`}
                  className={`kawaii-btn px-4 py-2 rounded-full text-xs font-body font-semibold border ${
                    active ? (o.v ? 'bg-[#E63946] text-white border-[#E63946]' : 'bg-kawaii-mint/60 text-kawaii-text border-kawaii-mint') : 'bg-white text-kawaii-textSoft border-kawaii-border'
                  }`}
                >{o.label}</button>
              );
            })}
          </div>
        </div>
        <div className="md:col-span-2">
          <label className="text-xs uppercase tracking-[0.2em] text-kawaii-textSoft font-semibold">Affected areas (optional)</label>
          <div className="mt-2 flex justify-center">
            <BodyDiagram selected={regions} onToggle={(k) => setRegions((r) => r.includes(k) ? r.filter((x) => x !== k) : [...r, k])} size={140} />
          </div>
        </div>
        <div className="md:col-span-2">
          <label className="text-xs uppercase tracking-[0.2em] text-kawaii-textSoft font-semibold">Notes (optional)</label>
          <input
            value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder="Antibiotic name, hospital, anything worth remembering"
            data-testid="past-notes"
            className="mt-2 w-full px-3 py-2 rounded-2xl border border-kawaii-border bg-white font-body text-sm text-kawaii-text focus:outline-none focus:ring-2 focus:ring-[#E63946]/60"
          />
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <button type="button" onClick={onCancel} className="kawaii-btn px-4 py-2 rounded-full bg-white border border-kawaii-border font-body font-semibold text-sm text-kawaii-text">Cancel</button>
        <button
          type="submit" disabled={saving}
          data-testid="save-past-cellulitis-btn"
          className="kawaii-btn px-5 py-2 rounded-full bg-[#E63946] text-white font-body font-semibold text-sm shadow-sm inline-flex items-center gap-2 disabled:opacity-60"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" strokeWidth={2.5} />}
          Save past episode
        </button>
      </div>
    </motion.form>
  );
}

export default function CellulitisTracker({ onClose, onChanged, initialPastDate }) {
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showPastForm, setShowPastForm] = useState(!!initialPastDate);
  const [openCheckin, setOpenCheckin] = useState(null); // episode.id
  const [showTimeline, setShowTimeline] = useState(null); // episode.id
  const [refreshToken, setRefreshToken] = useState(0);

  const [startDate, setStartDate] = useState(today());
  const [regions, setRegions] = useState([]);
  const [severity, setSeverity] = useState("early_onset");
  const [onAntibiotics, setOnAntibiotics] = useState(false);
  const [notes, setNotes] = useState("");
  const [hasTemperature, setHasTemperature] = useState(null);   // true/false/null
  const [temperatureC, setTemperatureC] = useState("");
  const [feelsAchy, setFeelsAchy] = useState(null);
  const [feelsFeverish, setFeelsFeverish] = useState(null);
  const [skinBreaks, setSkinBreaks] = useState(null);
  const [skinBreaksNotes, setSkinBreaksNotes] = useState("");
  const [warmToTouch, setWarmToTouch] = useState(null);
  const [redStreaks, setRedStreaks] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/cellulitis");
      setEpisodes(data);
    } catch { toast.error("Couldn't load episodes"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const active = useMemo(() => episodes.filter((e) => !e.end_date), [episodes]);
  const past = useMemo(() => episodes.filter((e) => e.end_date), [episodes]);

  const toggleRegion = (k) => {
    setRegions((r) => (r.includes(k) ? r.filter((x) => x !== k) : [...r, k]));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!regions.length) return toast.error("Tap the body diagram to mark affected areas");
    setCreating(true);
    try {
      const { data } = await api.post("/cellulitis", {
        start_date: startDate,
        end_date: null,
        body_parts: regions,
        severity,
        on_antibiotics: onAntibiotics,
        notes,
        has_temperature: hasTemperature,
        temperature_c: temperatureC ? parseFloat(temperatureC) : null,
        feels_achy: feelsAchy,
        feels_feverish: feelsFeverish,
        skin_breaks: skinBreaks,
        skin_breaks_notes: skinBreaksNotes,
        warm_to_touch: warmToTouch,
        red_streaks: redStreaks,
      });
      setEpisodes((prev) => [data, ...prev]);
      setShowForm(false);
      setRegions([]); setSeverity("early_onset"); setOnAntibiotics(false); setNotes(""); setStartDate(today());
      setHasTemperature(null); setTemperatureC(""); setFeelsAchy(null); setFeelsFeverish(null);
      setSkinBreaks(null); setSkinBreaksNotes(""); setWarmToTouch(null); setRedStreaks(null);
      toast.success("Episode logged — take care of yourself ♡");
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save");
    } finally { setCreating(false); }
  };

  const markResolved = async (id) => {
    try {
      const { data } = await api.patch(`/cellulitis/${id}`, { end_date: today() });
      setEpisodes((prev) => prev.map((e) => (e.id === id ? data : e)));
      toast.success("Marked resolved ♡ — glad you're on the other side of it");
      onChanged && onChanged();
    } catch { toast.error("Couldn't update"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this episode from your log?")) return;
    try {
      await api.delete(`/cellulitis/${id}`);
      setEpisodes((prev) => prev.filter((e) => e.id !== id));
      onChanged && onChanged();
    } catch { toast.error("Couldn't delete"); }
  };

  const toggleAntibiotics = async (id, v) => {
    try {
      const { data } = await api.patch(`/cellulitis/${id}`, { on_antibiotics: v });
      setEpisodes((prev) => prev.map((e) => (e.id === id ? data : e)));
    } catch { toast.error("Couldn't update"); }
  };

  const sharePdf = async (ep) => {
    try {
      const token = localStorage.getItem("lc_token");
      const res = await fetch(`${API}/cellulitis/${ep.id}/pdf?auth=${token}`);
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const filename = `cellulitis-${ep.start_date}.pdf`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF saved to your device ♡");

      // Load GP email + visit info and open mail client if we have somewhere to send
      let gpEmail = "", gpName = "", patientName = "";
      try {
        const p = (await api.get("/profile")).data;
        gpEmail = p.gp_email || p.clinic_email || "";
        gpName = p.gp_name || p.clinic_name || "";
        patientName = p.name || "";
      } catch {}

      // Was there a hospital visit for this episode?
      let visitSummary = "";
      try {
        const v = (await api.get(`/cellulitis/${ep.id}/visits`)).data || [];
        if (v.length) {
          const last = v[v.length - 1];
          const fmtT = (iso) => iso ? new Date(iso).toLocaleString() : "not logged";
          visitSummary = `\nUrgent care visit for this episode:\n• Arrived: ${fmtT(last.arrived_at)}\n• Triage: ${fmtT(last.triaged_at)}\n• Seen by doctor: ${fmtT(last.seen_by_doctor_at)}\n• IV antibiotics: ${fmtT(last.antibiotics_started_at)}\n• Discharged/admitted: ${fmtT(last.discharged_at)}\n`;
        }
      } catch {}

      const openMail = window.confirm(
        gpEmail
          ? `Send this PDF to ${gpName || 'your GP'} at ${gpEmail}? Your email app will open and you'll need to attach the just-downloaded '${filename}'.`
          : `No GP email on file. Add one in your Profile so future shares are one-tap. Open a blank draft anyway?`
      );
      if (!openMail) return;

      const subject = `Cellulitis episode summary — ${patientName || 'patient'} — ${ep.start_date}`;
      const body =
`Hello${gpName ? ' ' + gpName : ''},

I am a lymphedema patient. I have attached a PDF summary of an active cellulitis episode from MyLymphieLife (my self-tracking log).

Episode start: ${ep.start_date}
Severity (self-assessed): ${(ep.severity || 'moderate').toUpperCase()}
Currently on antibiotics: ${ep.on_antibiotics ? 'yes' : 'no'}
${visitSummary}
Please attach the file '${filename}' from your downloads before sending.

Kind regards,
${patientName || ''}
— Sent from MyLymphieLife`;

      const mailto = `mailto:${encodeURIComponent(gpEmail)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      window.location.href = mailto;
    } catch { toast.error("Couldn't generate PDF"); }
  };

  return (
    <motion.div
      className="fixed inset-0 z-50 backdrop-blur-xl bg-white/40 overflow-y-auto"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        onClick={(e) => e.stopPropagation()}
        initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        className="max-w-3xl mx-auto my-6 md:my-12 kawaii-card p-6 md:p-10"
        data-testid="cellulitis-tracker"
      >
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-9 h-9 rounded-2xl bg-gradient-to-br from-[#FF6B6B] to-[#E63946] flex items-center justify-center text-white">
                <ShieldAlert className="w-4 h-4" strokeWidth={2.5} />
              </div>
              <p className="text-xs uppercase tracking-[0.25em] text-kawaii-textSoft font-semibold">Cellulitis tracker</p>
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-kawaii-text">Body map & episodes</h2>
            <p className="text-sm text-kawaii-textSoft font-body mt-2">
              Log active infections. Days within an episode show as <span className="text-[#E63946] font-semibold">red</span> on your calendar.
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="close-cellulitis-btn"
            className="kawaii-btn w-10 h-10 rounded-full bg-white border border-kawaii-border flex items-center justify-center"
          >
            <X className="w-5 h-5" strokeWidth={2.5} />
          </button>
        </div>

        {/* Active episodes */}
        {active.length > 0 && (
          <div className="mb-6">
            <div className="text-[10px] uppercase tracking-[0.25em] text-kawaii-textSoft font-bold mb-2">Currently active</div>
            <div className="space-y-3">
              {active.map((ep) => (
                <div key={ep.id} className="rounded-2xl border-2 border-[#E63946]/40 bg-gradient-to-br from-[#FFE5E5]/50 to-white p-5" data-testid={`episode-active-${ep.id}`}>
                  <div className="flex items-start gap-4">
                    <BodyDiagram selected={ep.body_parts} readOnly size={110} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-heading font-semibold text-kawaii-text">
                          Started {format(parseISO(ep.start_date), "MMM d, yyyy")}
                        </h3>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider ${
                          ep.severity === 'severe' ? 'bg-[#E63946] text-white'
                          : 'bg-kawaii-peach/40 text-kawaii-textSoft'
                        }`}>{(ep.severity || 'early_onset').replace('_',' ')}</span>
                      </div>
                      <p className="text-sm text-kawaii-text/80 font-body mt-1.5">
                        {ep.body_parts.map((k) => REGION_LABEL[k] || k).join(", ")}
                      </p>
                      {ep.notes && <p className="text-xs text-kawaii-textSoft mt-1.5 font-body italic">"{ep.notes}"</p>}

                      {(ep.has_temperature || ep.feels_achy || ep.feels_feverish || ep.skin_breaks || ep.warm_to_touch || ep.red_streaks) && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {ep.has_temperature && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-body font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#FFE5E5] text-[#E63946]">
                              🌡️ Temp{ep.temperature_c ? ` ${ep.temperature_c}°` : ''}
                            </span>
                          )}
                          {ep.feels_feverish && (
                            <span className="text-[10px] font-body font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#FFE5E5] text-[#E63946]">Feverish</span>
                          )}
                          {ep.feels_achy && (
                            <span className="text-[10px] font-body font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-kawaii-peach/40 text-kawaii-textSoft">Achy</span>
                          )}
                          {ep.warm_to_touch && (
                            <span className="text-[10px] font-body font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-kawaii-peach/40 text-kawaii-textSoft">Warm</span>
                          )}
                          {ep.skin_breaks && (
                            <span className="text-[10px] font-body font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-kawaii-lavender/40 text-kawaii-text">Skin breaks</span>
                          )}
                          {ep.red_streaks && (
                            <span className="text-[10px] font-body font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#E63946] text-white">Red streaks ⚠</span>
                          )}
                        </div>
                      )}
                      <div className="mt-3 flex items-center gap-4 flex-wrap">
                        <label className="inline-flex items-center gap-2">
                          <Switch
                            checked={ep.on_antibiotics}
                            onCheckedChange={(v) => toggleAntibiotics(ep.id, v)}
                            data-testid={`abx-toggle-${ep.id}`}
                          />
                          <span className="text-xs font-body text-kawaii-text inline-flex items-center gap-1">
                            <Pill className="w-3 h-3" /> On antibiotics
                          </span>
                        </label>
                        <button
                          onClick={() => markResolved(ep.id)}
                          data-testid={`resolve-${ep.id}`}
                          className="kawaii-btn text-xs px-3 py-1.5 rounded-full bg-white border border-kawaii-mint/60 text-kawaii-text font-body font-semibold"
                        >
                          Mark resolved
                        </button>
                        <button
                          onClick={() => remove(ep.id)}
                          data-testid={`delete-episode-${ep.id}`}
                          className="kawaii-btn w-7 h-7 rounded-full bg-white border border-kawaii-border flex items-center justify-center"
                          title="Delete"
                        >
                          <Trash2 className="w-3 h-3 text-kawaii-textSoft" />
                        </button>
                      </div>

                      <div className="mt-3 flex flex-wrap gap-2">
                        <button
                          onClick={() => setOpenCheckin(openCheckin === ep.id ? null : ep.id)}
                          data-testid={`toggle-checkin-${ep.id}`}
                          className="kawaii-btn text-xs px-3 py-1.5 rounded-full bg-gradient-to-r from-[#FF6B6B] to-[#E63946] text-white font-body font-semibold inline-flex items-center gap-1.5"
                        >
                          <ClipboardCheck className="w-3.5 h-3.5" strokeWidth={2.5} />
                          {openCheckin === ep.id ? "Close check-in" : "Daily check-in"}
                        </button>
                        <button
                          onClick={() => setShowTimeline(showTimeline === ep.id ? null : ep.id)}
                          data-testid={`toggle-timeline-${ep.id}`}
                          className="kawaii-btn text-xs px-3 py-1.5 rounded-full bg-white border border-kawaii-border text-kawaii-text font-body font-semibold inline-flex items-center gap-1.5"
                        >
                          {showTimeline === ep.id ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                          Timeline
                        </button>
                        <button
                          onClick={() => sharePdf(ep)}
                          data-testid={`share-pdf-${ep.id}`}
                          className="kawaii-btn text-xs px-3 py-1.5 rounded-full bg-white border border-kawaii-lavender/60 text-kawaii-text font-body font-semibold inline-flex items-center gap-1.5"
                        >
                          <FileDown className="w-3.5 h-3.5" strokeWidth={2.5} /> Share with doctor
                        </button>
                      </div>

                      {openCheckin === ep.id && (
                        <CheckinForm
                          episode={ep}
                          onSaved={(updated) => {
                            setEpisodes((prev) => prev.map((e) =>
                              e.id === ep.id ? { ...e,
                                has_temperature: updated.has_temperature,
                                temperature_c: updated.temperature_c,
                                feels_achy: updated.feels_achy,
                                feels_feverish: updated.feels_feverish,
                                skin_breaks: updated.skin_breaks,
                                warm_to_touch: updated.warm_to_touch,
                                red_streaks: updated.red_streaks,
                              } : e
                            ));
                            setRefreshToken((t) => t + 1);
                            setOpenCheckin(null);
                            setShowTimeline(ep.id);
                            onChanged && onChanged();
                          }}
                        />
                      )}

                      {showTimeline === ep.id && (
                        <CheckinTimeline episodeId={ep.id} refreshToken={refreshToken} />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Add form */}
        {showForm ? (
          <motion.form
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            onSubmit={submit}
            className="rounded-2xl bg-kawaii-bg/60 border border-kawaii-border p-5 mb-6"
            data-testid="cellulitis-form"
          >
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-kawaii-textSoft font-semibold">Tap affected areas</label>
                <div className="mt-3">
                  <BodyDiagram selected={regions} onToggle={toggleRegion} />
                </div>
                <p className="text-[11px] text-kawaii-textSoft text-center mt-2">
                  {regions.length ? `${regions.length} area(s) selected` : "Tap any body region"}
                </p>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-kawaii-textSoft font-semibold">Started on</label>
                  <input
                    type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                    max={today()}
                    data-testid="cellulitis-start-date"
                    className="mt-2 w-full px-4 py-2.5 rounded-2xl border border-kawaii-border bg-white font-body text-kawaii-text focus:outline-none focus:ring-2 focus:ring-[#E63946]/60"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-kawaii-textSoft font-semibold">Stage</label>
                  <p className="text-[10px] text-kawaii-textSoft/70 mt-0.5 mb-2 font-body italic">Auto-updates from your symptom check-in</p>
                  <div className="flex gap-2">
                    {SEVERITIES.map((s) => (
                      <button
                        type="button" key={s.key}
                        onClick={() => setSeverity(s.key)}
                        data-testid={`severity-${s.key}`}
                        className={`kawaii-btn flex-1 py-2 px-2 rounded-2xl text-left border ${
                          severity === s.key ? 'bg-[#E63946] text-white border-[#E63946]' : 'bg-white text-kawaii-text border-kawaii-border'
                        }`}
                      >
                        <div className="text-xs font-heading font-semibold uppercase tracking-wider">{s.label}</div>
                        <div className={`text-[10px] font-body mt-0.5 ${severity === s.key ? 'text-white/85' : 'text-kawaii-textSoft'}`}>{s.hint}</div>
                      </button>
                    ))}
                  </div>
                </div>
                <label className="flex items-center gap-3 rounded-2xl bg-white border border-kawaii-border p-3">
                  <Switch
                    checked={onAntibiotics}
                    onCheckedChange={setOnAntibiotics}
                    data-testid="cellulitis-abx"
                  />
                  <div>
                    <div className="font-heading text-sm font-semibold text-kawaii-text">On antibiotics</div>
                    <div className="text-xs text-kawaii-textSoft">Track your course</div>
                  </div>
                </label>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-kawaii-textSoft font-semibold">Notes</label>
                  <textarea
                    value={notes} onChange={(e) => setNotes(e.target.value)}
                    rows={2}
                    placeholder="Antibiotic name, symptoms, doctor visit..."
                    data-testid="cellulitis-notes"
                    className="mt-2 w-full px-3 py-2 rounded-2xl border border-kawaii-border bg-white font-body text-sm text-kawaii-text focus:outline-none focus:ring-2 focus:ring-[#E63946]/60 resize-none"
                  />
                </div>
              </div>
            </div>

            {/* Symptom check-in */}
            <div className="mt-6 pt-5 border-t border-kawaii-border">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-[10px] uppercase tracking-[0.25em] text-kawaii-textSoft font-bold">Symptom check-in</span>
                <span className="text-[10px] text-kawaii-textSoft/70 font-body italic">— tap any that apply, no pressure ♡</span>
              </div>
              <div className="grid md:grid-cols-2 gap-2">
                <SymptomRow
                  label="Do you have a temperature?"
                  value={hasTemperature} onChange={setHasTemperature}
                  testId="sym-temperature" danger
                />
                <div className={`flex items-center justify-between gap-3 p-3 rounded-2xl border ${
                  hasTemperature === true ? 'border-[#E63946]/60 bg-[#FFE5E5]/40' : 'border-kawaii-border bg-white'
                }`}>
                  <span className="text-sm font-body text-kawaii-text pr-2">Reading (°C, optional)</span>
                  <div className="flex items-center gap-2 flex-wrap justify-end">
                    <input
                      type="number" step="0.1" min="34" max="43"
                      value={temperatureC}
                      onChange={(e) => setTemperatureC(e.target.value)}
                      placeholder="37.5"
                      data-testid="sym-temperature-value"
                      className="w-20 px-2 py-1 rounded-xl border border-kawaii-border bg-white font-body text-sm text-kawaii-text text-center focus:outline-none focus:ring-2 focus:ring-[#E63946]/60"
                    />
                    <BluetoothTempButton onRead={(c) => { setTemperatureC(c); setHasTemperature(true); }} testId="sym-bt-thermo" />
                    <button
                      type="button"
                      onClick={() => setTemperatureC("")}
                      data-testid="sym-temperature-unknown"
                      className={`kawaii-btn text-[11px] px-2.5 py-1 rounded-full font-body font-semibold border ${
                        temperatureC === "" && hasTemperature === true ? 'bg-kawaii-lavender/60 border-kawaii-lavender text-kawaii-text' : 'bg-white border-kawaii-border text-kawaii-textSoft'
                      }`}
                    >Don't know</button>
                  </div>
                </div>
                <SymptomRow
                  label="Are you feeling achy?"
                  value={feelsAchy} onChange={setFeelsAchy}
                  testId="sym-achy"
                />
                <SymptomRow
                  label="Are you feeling feverish?"
                  value={feelsFeverish} onChange={setFeelsFeverish}
                  testId="sym-feverish" danger
                />
                <SymptomRow
                  label="Any breaks in the skin?"
                  value={skinBreaks} onChange={setSkinBreaks}
                  testId="sym-skin-breaks"
                />
                <SymptomRow
                  label="Warm to the touch?"
                  value={warmToTouch} onChange={setWarmToTouch}
                  testId="sym-warm"
                />
                <SymptomRow
                  label="Red streaks spreading?"
                  value={redStreaks} onChange={setRedStreaks}
                  testId="sym-streaks" danger
                />
              </div>
              {skinBreaks === true && (
                <input
                  value={skinBreaksNotes}
                  onChange={(e) => setSkinBreaksNotes(e.target.value)}
                  placeholder="Where? Any cuts, cracks, insect bites?"
                  data-testid="sym-skin-breaks-notes"
                  className="mt-2 w-full px-3 py-2 rounded-2xl border border-kawaii-border bg-white font-body text-sm text-kawaii-text focus:outline-none focus:ring-2 focus:ring-[#E63946]/60"
                />
              )}
              {(hasTemperature === true || feelsFeverish === true || redStreaks === true) && (
                <div className="mt-4 rounded-2xl bg-[#FFE5E5]/60 border border-[#E63946]/50 p-3.5 text-sm text-kawaii-text font-body" data-testid="urgent-care-notice">
                  <div className="flex items-start gap-2">
                    <span className="text-lg leading-none">⚠️</span>
                    <div className="flex-1">
                      <div className="font-heading font-semibold text-[#E63946] leading-tight">Please contact your care team</div>
                      <p className="mt-1 text-xs leading-relaxed text-kawaii-textSoft">
                        Fever, feverish feeling, or spreading red streaks with cellulitis can signal a systemic infection. Reach out to your GP, lymphedema nurse, or urgent care today.
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          const symptoms = [];
                          if (hasTemperature === true) symptoms.push(`a temperature${temperatureC ? ` of ${temperatureC}°C` : ''}`);
                          if (feelsFeverish === true) symptoms.push("feeling feverish");
                          if (redStreaks === true) symptoms.push("red streaks spreading");
                          const seed = `Hey — I noticed you're logging cellulitis with ${symptoms.join(' and ')}. That combination can point to a systemic infection and needs same-day medical attention. Please call your GP, lymphedema nurse, or head to urgent care. I'm right here while you sort it out ♡`;
                          window.dispatchEvent(new CustomEvent("open-lymphie", { detail: { seedMessage: seed } }));
                        }}
                        data-testid="urgent-talk-to-lymphie"
                        className="kawaii-btn mt-3 px-4 py-1.5 rounded-full bg-gradient-to-r from-[#FF6B6B] to-[#E63946] text-white text-xs font-heading font-semibold shadow-sm inline-flex items-center gap-2"
                      >
                        💬 Talk to Lymphie now ♡
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 mt-5">
              <button
                type="button" onClick={() => setShowForm(false)}
                className="kawaii-btn px-4 py-2 rounded-full bg-white border border-kawaii-border font-body font-semibold text-sm text-kawaii-text"
              >Cancel</button>
              <button
                type="submit" disabled={creating}
                data-testid="save-cellulitis-btn"
                className="kawaii-btn px-5 py-2 rounded-full bg-gradient-to-r from-[#FF6B6B] to-[#E63946] text-white font-body font-semibold text-sm shadow-sm disabled:opacity-60 inline-flex items-center gap-2"
              >
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" strokeWidth={2.5} />}
                Log episode
              </button>
            </div>
          </motion.form>
        ) : (
          <div className="grid md:grid-cols-2 gap-3 mb-6">
            <button
              onClick={() => setShowForm(true)}
              data-testid="add-cellulitis-btn"
              className="kawaii-btn py-3 rounded-2xl bg-gradient-to-r from-[#FF6B6B] to-[#E63946] text-white font-heading font-semibold shadow-md inline-flex items-center justify-center gap-2"
            >
              <Plus className="w-5 h-5" strokeWidth={2.5} /> Log a new episode
            </button>
            <button
              onClick={() => setShowPastForm(true)}
              data-testid="add-past-cellulitis-btn"
              className="kawaii-btn py-3 rounded-2xl bg-white border border-[#E63946]/40 text-[#E63946] font-heading font-semibold inline-flex items-center justify-center gap-2"
            >
              <Plus className="w-5 h-5" strokeWidth={2.5} /> Log a past episode
            </button>
          </div>
        )}

        {showPastForm && (
          <PastEpisodeForm
            initialDate={initialPastDate}
            onCancel={() => setShowPastForm(false)}
            onSaved={(ep) => { setEpisodes((prev) => [ep, ...prev]); setShowPastForm(false); onChanged && onChanged(); }}
          />
        )}

        {/* Past episodes */}
        {past.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-kawaii-textSoft font-bold mb-2">Past episodes</div>
            <div className="space-y-2">
              {past.map((ep) => (
                <div key={ep.id} className="flex items-center justify-between p-3 rounded-2xl bg-white border border-kawaii-border" data-testid={`episode-past-${ep.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-xl bg-kawaii-mint/40 flex items-center justify-center text-lg">✿</div>
                    <div className="min-w-0">
                      <div className="text-sm font-heading font-semibold text-kawaii-text flex items-center gap-2 flex-wrap">
                        {format(parseISO(ep.start_date), "MMM d")} – {format(parseISO(ep.end_date), "MMM d, yyyy")}
                        {ep.severity === 'severe' && (
                          <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-[#E63946] text-white">Severe</span>
                        )}
                        {ep.hospitalized && (
                          <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-kawaii-lavender/50 text-kawaii-text">Hospitalized</span>
                        )}
                      </div>
                      <div className="text-xs text-kawaii-textSoft truncate">
                        {(ep.body_parts || []).map((k) => REGION_LABEL[k] || k).join(", ") || "—"}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => remove(ep.id)}
                    className="kawaii-btn w-8 h-8 rounded-full bg-white border border-kawaii-border flex items-center justify-center"
                  >
                    <Trash2 className="w-3.5 h-3.5 text-kawaii-textSoft" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {!loading && episodes.length === 0 && !showForm && (
          <div className="rounded-2xl border-2 border-dashed border-kawaii-border p-8 text-center">
            <div className="text-4xl mb-2">🌿</div>
            <p className="text-kawaii-textSoft font-body text-sm">No cellulitis episodes logged. Take good care ♡</p>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}
